import React from 'react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Download, FileText, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const downloadFile = (content, filename, mimeType = 'text/plain') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40);

const formatForStanStore = (content, promptKey) => {
  return `# Stan Store Export
<!-- Generated by Creataby -->
<!-- Prompt: ${promptKey} -->

---

## How to use this file

1. Open your Stan Store dashboard
2. Create a new product or landing page
3. Copy each section below into the matching field

---

<!-- HEADLINE — Paste into your Stan Store title/headline -->

${extractSection(content, 'Headline', 'headline') || extractFirstHeading(content) || 'Your Headline Here'}

---

<!-- DESCRIPTION — Paste into your product description -->

${extractSection(content, 'Opening Hook', 'opening') || extractSection(content, 'The Solution', 'solution') || extractBodyContent(content)}

---

<!-- WHAT'S INCLUDED — Paste into your deliverables/features section -->

${extractSection(content, "What's Included", 'included') || extractSection(content, 'What\'s Inside', 'inside') || extractSection(content, 'Key Benefits', 'benefits') || ''}

---

<!-- CALL TO ACTION — Use as your button text -->

${extractSection(content, 'Call to Action', 'cta') || extractSection(content, 'CTA', 'cta') || 'Get Started Now'}

---

<!-- FULL CONTENT — Complete generated content for reference -->

${content}
`;
};

const formatForGumroad = (content, promptKey) => {
  return `# Gumroad Product Export
<!-- Generated by Creataby -->
<!-- Prompt: ${promptKey} -->

---

## How to use this file

1. Go to gumroad.com/products → New Product
2. Copy each section below into the matching Gumroad field

---

<!-- PRODUCT NAME — Paste into "Name" field -->

${extractFirstHeading(content) || 'Your Product Name'}

---

<!-- SHORT DESCRIPTION — Paste into the product summary (keep under 140 chars) -->

${extractShortDescription(content)}

---

<!-- FULL DESCRIPTION — Paste into the rich text product description -->

${content}

---

<!-- PRICE SUGGESTION -->

${extractSection(content, 'Price', 'pricing') || extractSection(content, 'Pricing', 'pricing') || 'Set your price in the Gumroad dashboard'}

---

<!-- CALL TO ACTION — Use as your "I want this!" button text -->

${extractSection(content, 'Call to Action', 'cta') || extractSection(content, 'CTA', 'cta') || 'I want this!'}
`;
};

const formatForSystemeIo = (content, promptKey) => {
  return `# Systeme.io Funnel Export
<!-- Generated by Creataby -->
<!-- Prompt: ${promptKey} -->

---

## How to use this file

1. In Systeme.io, go to Funnels → Create
2. Choose a template, then replace each section with the content below
3. Each section maps to a common funnel page block

---

<!-- ABOVE THE FOLD — Hero section headline + subheadline -->

${extractSection(content, 'Headline', 'headline') || extractFirstHeading(content) || 'Your Headline'}

${extractSection(content, 'Subheadline', 'sub') || ''}

---

<!-- PROBLEM SECTION — Pain point block -->

${extractSection(content, 'The Problem', 'problem') || extractSection(content, 'Problem', 'problem') || ''}

---

<!-- SOLUTION SECTION — Your offer introduction -->

${extractSection(content, 'The Solution', 'solution') || extractSection(content, 'Solution', 'solution') || ''}

---

<!-- FEATURES / BENEFITS — Bullet point block -->

${extractSection(content, "What's Included", 'included') || extractSection(content, 'What\'s Inside', 'inside') || extractSection(content, 'Key Benefits', 'benefits') || ''}

---

<!-- SOCIAL PROOF / FAQ — Trust-building section -->

${extractSection(content, 'FAQ', 'faq') || extractSection(content, 'Guarantee', 'guarantee') || ''}

---

<!-- CTA BUTTON — Final call to action -->

${extractSection(content, 'Call to Action', 'cta') || extractSection(content, 'CTA', 'cta') || 'Get Started Now'}

---

<!-- EMAIL SEQUENCE — If included, set up in Systeme.io email campaigns -->

${extractSection(content, 'Email 1', 'email') || extractSection(content, 'EMAIL SEQUENCE', 'email') || '(No email sequence in this export)'}

---

<!-- FULL CONTENT — Complete generated content for reference -->

${content}
`;
};

const formatForCarrd = (content, promptKey) => {
  return `# Carrd Page Export
<!-- Generated by Creataby -->
<!-- Prompt: ${promptKey} -->

---

## How to use this file

1. Go to carrd.co → New Site → choose a template
2. Carrd uses simple sections — copy each block below into the matching section
3. Keep text short — Carrd works best with concise content

---

<!-- HERO HEADING — Main title section -->

${extractFirstHeading(content) || 'Your Headline'}

---

<!-- HERO SUBTEXT — One-liner under the heading -->

${extractShortDescription(content)}

---

<!-- CONTENT SECTIONS — Add as text blocks -->

${extractBodyContent(content).slice(0, 2000)}

---

<!-- CTA BUTTON TEXT -->

${extractSection(content, 'Call to Action', 'cta') || extractSection(content, 'CTA', 'cta') || 'Get Started'}

---

<!-- FULL CONTENT — Complete reference (Carrd may need trimming) -->

${content}
`;
};

// Helper: extract a markdown section by heading name
function extractSection(content, headingName) {
  const patterns = [
    new RegExp(`##\\s*${headingName}[\\s:]*\\n([\\s\\S]*?)(?=\\n##|$)`, 'i'),
    new RegExp(`\\*\\*${headingName}\\*\\*[:\\s]*\\n([\\s\\S]*?)(?=\\n\\*\\*|\\n##|$)`, 'i'),
  ];
  for (const regex of patterns) {
    const match = content.match(regex);
    if (match && match[1]?.trim()) return match[1].trim();
  }
  return '';
}

// Helper: get the first heading text
function extractFirstHeading(content) {
  const match = content.match(/^##?\s+(.+)$/m);
  return match ? match[1].trim() : '';
}

// Helper: get a short description (first non-heading paragraph, under 140 chars)
function extractShortDescription(content) {
  const lines = content.split('\n').filter(l => l.trim() && !l.startsWith('#') && !l.startsWith('---'));
  const first = lines[0] || '';
  return first.length > 140 ? first.slice(0, 137) + '...' : first;
}

// Helper: get the body content without the first heading
function extractBodyContent(content) {
  const lines = content.split('\n');
  const startIdx = lines.findIndex((l, i) => i > 0 && l.trim());
  return lines.slice(Math.max(startIdx, 1)).join('\n').trim();
}

export const ExportDropdown = ({ content, promptKey, size = 'sm' }) => {
  if (!content) return null;

  const fileSlug = slugify(promptKey || 'export');

  const handleDownloadMd = () => {
    downloadFile(content, `${fileSlug}.md`, 'text/markdown');
    toast.success('Downloaded as .md');
  };

  const handleDownloadTxt = () => {
    // Strip markdown formatting for plain text
    const plain = content
      .replace(/#{1,6}\s+/g, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/---/g, '')
      .replace(/\n{3,}/g, '\n\n');
    downloadFile(plain, `${fileSlug}.txt`, 'text/plain');
    toast.success('Downloaded as .txt');
  };

  const handleExportStanStore = () => {
    downloadFile(formatForStanStore(content, promptKey), `${fileSlug}_stan-store.md`, 'text/markdown');
    toast.success('Exported for Stan Store');
  };

  const handleExportGumroad = () => {
    downloadFile(formatForGumroad(content, promptKey), `${fileSlug}_gumroad.md`, 'text/markdown');
    toast.success('Exported for Gumroad');
  };

  const handleExportSystemeIo = () => {
    downloadFile(formatForSystemeIo(content, promptKey), `${fileSlug}_systeme-io.md`, 'text/markdown');
    toast.success('Exported for Systeme.io');
  };

  const handleExportCarrd = () => {
    downloadFile(formatForCarrd(content, promptKey), `${fileSlug}_carrd.md`, 'text/markdown');
    toast.success('Exported for Carrd');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size={size} className="h-8 text-xs" data-testid="export-dropdown-btn">
          <Share2 className="h-3.5 w-3.5 mr-1" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Download</DropdownMenuLabel>
        <DropdownMenuItem onClick={handleDownloadMd} data-testid="export-md">
          <FileText className="h-4 w-4 mr-2 text-teal-400" />
          Download .md
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDownloadTxt} data-testid="export-txt">
          <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
          Download .txt
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">Export for Funnel Builder</DropdownMenuLabel>

        <DropdownMenuItem onClick={handleExportStanStore} data-testid="export-stan-store">
          <Download className="h-4 w-4 mr-2 text-teal-400" />
          Stan Store
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportGumroad} data-testid="export-gumroad">
          <Download className="h-4 w-4 mr-2 text-teal-400" />
          Gumroad
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportSystemeIo} data-testid="export-systeme-io">
          <Download className="h-4 w-4 mr-2 text-teal-400" />
          Systeme.io
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportCarrd} data-testid="export-carrd">
          <Download className="h-4 w-4 mr-2 text-teal-400" />
          Carrd
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
